// TriggerWatch Dashboard — landing page for the TriggerWatch section
// Shows deployment activity at a glance: success/failure rates, most-used packages,
// most-targeted devices, and a 7-day trend

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const API_URL = config.API_URL;

export default function TriggerWatchDashboard() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    axios.get(`${API_URL}/organizations/${ORG_ID}/tw-summary`, authHeaders)
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);

  function statusBadge(status) {
    if (status === "completed") return "bg-green-900 text-green-300";
    if (status === "partial") return "bg-yellow-900 text-yellow-300";
    if (status === "failed") return "bg-red-900 text-red-300";
    if (status === "in_progress") return "bg-blue-900 text-blue-300";
    if (status === "cancelled") return "bg-pink-900 text-pink-300";
    return "bg-gray-700 text-gray-400";
  }

  const trendData = summary?.trend.map(d => ({
    date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    succeeded: d.succeeded,
    failed: d.failed
  })) || [];

  if (loading) return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar orgName={storedUser.organization_name} />
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Loading...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar orgName={storedUser.organization_name} />

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">TriggerWatch</h1>
            <p className="text-gray-400 text-sm">Package deployment activity, last 7 days</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/triggerwatch/packages")}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              Packages
            </button>
            <button
              onClick={() => navigate("/triggerwatch/deployments")}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              Deployments
            </button>
            <button
              onClick={() => navigate("/triggerwatch/device-groups")}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              Device Groups
            </button>
          </div>
        </div>

        {!summary || summary.total_deployments === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No deployments yet. Create a package and deploy it to get started.
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <p className="text-gray-400 text-sm mb-1">Total Deployments</p>
                <p className="text-3xl font-bold text-white">{summary.total_deployments}</p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 border border-green-900">
                <p className="text-gray-400 text-sm mb-1">Succeeded</p>
                <p className="text-3xl font-bold text-green-400">{summary.succeeded}</p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
                <p className="text-gray-400 text-sm mb-1">Failed</p>
                <p className="text-3xl font-bold text-red-400">{summary.failed}</p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                <p className="text-gray-400 text-sm mb-1">Pending</p>
                <p className="text-3xl font-bold text-gray-300">{summary.pending}</p>
              </div>
            </div>

            {/* Trend chart */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-8">
              <h2 className="text-white font-semibold mb-4">7 Day Trend</h2>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" tick={{ fill: "#9CA3AF", fontSize: 11 }} tickLine={false} />
                  <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#1F2937", border: "1px solid #374151", borderRadius: "8px" }} labelStyle={{ color: "#F9FAFB" }} itemStyle={{ color: "#D1D5DB" }} />
                  <Legend wrapperStyle={{ color: "#9CA3AF", fontSize: "12px" }} />
                  <Bar dataKey="succeeded" fill="#22C55E" name="Succeeded" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" fill="#EF4444" name="Failed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Most deployed / most targeted */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h2 className="text-white font-semibold mb-4">Most Deployed Packages</h2>
                {summary.most_deployed_packages.length === 0 ? (
                  <p className="text-gray-500 text-sm">No data yet.</p>
                ) : (
                  <div className="space-y-3">
                    {summary.most_deployed_packages.map((p, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">{p.package_name}</span>
                        <span className="text-gray-500 text-xs">{p.count} deployment{p.count !== 1 ? "s" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h2 className="text-white font-semibold mb-4">Most Targeted Devices</h2>
                {summary.most_targeted_devices.length === 0 ? (
                  <p className="text-gray-500 text-sm">No data yet.</p>
                ) : (
                  <div className="space-y-3">
                    {summary.most_targeted_devices.map((d, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-gray-300 text-sm">{d.hostname}</span>
                        <span className="text-gray-500 text-xs">{d.count} deployment{d.count !== 1 ? "s" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent deployments */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold">Recent Deployments</h2>
                <button
                  onClick={() => navigate("/triggerwatch/deployments")}
                  className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                >
                  View all →
                </button>
              </div>
              <div className="space-y-3">
                {summary.recent_deployments.map(dep => (
                  <div
                    key={dep.id}
                    onClick={() => navigate("/triggerwatch/deployments")}
                    className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0 cursor-pointer hover:bg-gray-800 -mx-2 px-2 rounded transition-colors"
                  >
                    <div>
                      <p className="text-white text-sm font-medium">{dep.package_name}</p>
                      <p className="text-gray-500 text-xs">
                        {dep.created_by} · {new Date(dep.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 text-xs">{dep.succeeded}/{dep.total_devices} succeeded</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(dep.status)}`}>{dep.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}